import { ChevronLeft, ChevronRight, Eye, Package, Inbox, Loader2 } from 'lucide-react';
import type { PromotedListing } from '@/client/types/promotion';
import { ListingThumb, numberFmt, RiskBadge } from './atoms';
import { money } from './listingMetrics';

const FillMeter: React.FC<{ fill: number }> = ({ fill }) => {
  const pct = Math.min(100, Math.round(fill * 100));
  const color = pct >= 75 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums text-xs text-gray-500">{pct}%</span>
    </div>
  );
};

const SkeletonRows: React.FC<{ rows: number }> = ({ rows }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-gray-200" />
            <div className="space-y-1.5">
              <div className="h-3 w-48 rounded bg-gray-200" />
              <div className="h-2.5 w-32 rounded bg-gray-100" />
            </div>
          </div>
        </td>
        {[0, 1, 2, 3, 4].map((c) => (
          <td key={c} className="px-5 py-3">
            <div className="h-3 w-16 rounded bg-gray-100" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export const ListingsTable: React.FC<{
  rows: PromotedListing[];
  page: number;
  pageCount: number;
  pageSize: number;
  isLoading?: boolean;
  isFetching?: boolean;
  onPage: (page: number) => void;
  onOpen: (eid: number) => void;
}> = ({ rows, page, pageCount, pageSize, isLoading, isFetching, onPage, onOpen }) => {
  if (!isLoading && rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white py-16 text-center shadow-sm">
        <Inbox className="mb-3 h-8 w-8 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">No listings match these filters</p>
        <p className="mt-1 text-xs text-gray-400">Try clearing the risk chips or search.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wide text-gray-400">
              <th className="px-5 py-3 font-medium">Listing</th>
              <th className="px-5 py-3 font-medium">Packages</th>
              <th className="px-5 py-3 font-medium">Impressions</th>
              <th className="px-5 py-3 font-medium">Fill</th>
              <th className="px-5 py-3 text-right font-medium">Spend</th>
              <th className="px-5 py-3 text-right font-medium">Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <SkeletonRows rows={pageSize} />
            ) : (
              rows.map((row) => (
                <tr
                  key={row.eid}
                  onClick={() => onOpen(row.eid)}
                  className="cursor-pointer transition-colors hover:bg-gray-50"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <ListingThumb cover={row.listing.cover} name={row.listing.name_basic} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{row.listing.name_basic}</p>
                        <p className="truncate text-[11px] text-gray-500">
                          {row.listing.type_name} · {row.listing.owner.name} · EID {row.eid}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5 text-gray-400" />
                      {row.package_count}
                      {row.active_count > 0 && (
                        <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                          {row.active_count} active
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="h-3.5 w-3.5 text-gray-400" />
                      {numberFmt.format(row.impressions)}
                      <span className="text-gray-400">/ {numberFmt.format(row.impression_target)}</span>
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3">
                    <FillMeter fill={row.fill} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums text-gray-900">
                    {money(row.total_spend)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <RiskBadge risk={row.risk} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-sm">
          <span className="inline-flex items-center gap-2 text-xs text-gray-500">
            Page {page} of {pageCount}
            {isFetching && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <button
              onClick={() => onPage(Math.min(pageCount, page + 1))}
              disabled={page >= pageCount}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
